import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io'
import type { LiveCheckinLevel, LiveCheckinMonth } from '../types/check-in'

interface Props {
  month: LiveCheckinMonth
  level: LiveCheckinLevel | null
  levelIndex: number
  totalLevels: number
  tasksCompleted: number
  tasksTotal: number
  onPrev: () => void
  onNext: () => void
}

/**
 * Header do carrossel — estilo rocket-game-modal.
 * Mostra o nível atual selecionado (não necessariamente o nível conquistado pelo user)
 * com setas para navegar. Indica também o nível conquistado (levelCurrent do mês).
 */
export const LevelCarouselHeader = memo(
  ({
    month,
    level,
    levelIndex,
    totalLevels,
    tasksCompleted,
    tasksTotal,
    onPrev,
    onNext,
  }: Props) => {
    const { t } = useTranslation()

    const levelName = level?.nameKey
      ? t(level.nameKey, { defaultValue: level.internalName ?? '' })
      : (level?.internalName ?? '')

    const isFirst = levelIndex <= 0
    const isLast = levelIndex >= totalLevels - 1
    const isLevelDone = tasksTotal > 0 && tasksCompleted >= tasksTotal
    const isCurrentUserLevel =
      level?.level !== undefined && level.level === (month.levelCurrent ?? 0)

    const pct =
      tasksTotal > 0 ? Math.min(100, (tasksCompleted / tasksTotal) * 100) : 0

    let chipBg = 'bg-violet-500/80'
    let chipText = `Nível ${level?.level ?? '—'}`
    if (isLevelDone) {
      chipBg = 'bg-emerald-500/90'
      chipText = `Nível ${level?.level ?? '—'} concluído`
    } else if (isCurrentUserLevel) {
      chipBg = 'bg-amber-400/90'
      chipText = `Nível ${level?.level ?? '—'} — atual`
    }

    return (
      <div className="w-full">
        {/* navegação + card central (compacto) */}
        <div className="flex items-center gap-2">
          <button
            onClick={onPrev}
            disabled={isFirst}
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/10 backdrop-blur-md transition active:scale-95 ${
              isFirst ? 'opacity-30' : 'hover:bg-white/20'
            }`}
            aria-label="prev level"
          >
            <IoIosArrowBack className="h-5 w-5 text-white" />
          </button>

          <div
            className={`min-w-0 flex-1 rounded-2xl border px-3 py-2 backdrop-blur-md ${
              isLevelDone
                ? 'border-emerald-300/40 bg-gradient-to-br from-emerald-400/25 via-emerald-500/20 to-emerald-700/25'
                : isCurrentUserLevel
                  ? 'border-amber-300/40 bg-gradient-to-br from-amber-400/25 via-orange-500/20 to-fuchsia-600/25'
                  : 'border-white/15 bg-gradient-to-br from-white/10 to-white/5'
            }`}
          >
            {/* linha topo: nível + chip de status + % */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2">
                <span className="text-xl font-extrabold leading-none text-white">
                  {level?.level ?? '—'}
                </span>
                {levelName && (
                  <span className="truncate text-xs font-semibold text-white/90">
                    {levelName}
                  </span>
                )}
              </div>
              <span
                className={`shrink-0 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white ${chipBg}`}
              >
                {chipText}
              </span>
            </div>

            {/* barra de progresso do nível */}
            <div className="mt-1.5">
              <div className="mb-0.5 flex items-center justify-between text-[10px] text-white/80">
                <span>
                  {tasksCompleted}/{tasksTotal} tarefas
                </span>
                <span>{Math.round(pct)}%</span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-black/30">
                <div
                  className={`h-full rounded-full bg-gradient-to-r transition-[width] duration-700 ease-out ${
                    isLevelDone
                      ? 'from-emerald-300 to-emerald-500'
                      : 'from-amber-300 via-orange-400 to-fuchsia-500'
                  }`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          </div>

          <button
            onClick={onNext}
            disabled={isLast}
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/10 backdrop-blur-md transition active:scale-95 ${
              isLast ? 'opacity-30' : 'hover:bg-white/20'
            }`}
            aria-label="next level"
          >
            <IoIosArrowForward className="h-5 w-5 text-white" />
          </button>
        </div>
      </div>
    )
  },
)

LevelCarouselHeader.displayName = 'LevelCarouselHeader'
